import {
  Button,
  DialogContent,
  DialogTitle,
  Modal,
  ModalClose,
  ModalDialog,
  DialogActions,
  Stack,
} from "@mui/joy";
import { useTranslation } from "../../../i18n";
import { useSupplierUpsertMutation } from "../../../__generated__/types-and-hooks";
import { useCallback, useEffect, useState } from "react";
import { useTeam } from "../TeamLayout";
import { useNotify } from "../../../hooks/useNotify";
import QTextInput from "../../../x-components/QTextInput";

export interface SupplierAddModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (supplier: any) => void;
}

class Supplier {
  public name: string = "";
  public addressLine1: string = "";
  public addressLine2: string = "";
  public city: string = "";
  public postalCode: string = "";
  public country: string = "US";

  constructor(public teamId: number) {}
}

export default function SupplierAddModal({
  open,
  onClose,
  onSave,
}: SupplierAddModalProps) {
  const teamCtx = useTeam();
  const [supplier, setSupplier] = useState(new Supplier(teamCtx.team.id));

  useEffect(() => {
    setSupplier(new Supplier(teamCtx.team.id));
  }, [open, teamCtx.team]);

  const t = useTranslation();

  const [supplierUpsertMutation, { loading }] = useSupplierUpsertMutation({});

  const [saving, setSaving] = useState(false);
  const notify = useNotify();

  const handleSave = useCallback(async () => {
    setSaving(true);
    await supplierUpsertMutation({
      variables: {
        supplier: {
          teamId: supplier.teamId,
          name: supplier.name,
          addressLine1: supplier.addressLine1,
          addressLine2: supplier.addressLine2,
          countryCode: supplier.country,
          postalCode: supplier.postalCode,
          city: supplier.city,
          contacts: [],
          capabilities: [],
        },
      },
      onCompleted(sup) {
        notify({
          type: "success",
          msg: `Supplier ${sup.supplier_upsert.uniqueId} created`,
        });
        onSave(sup.supplier_upsert);
      },
    });
    setSaving(false);
  }, [supplierUpsertMutation, supplier, notify, onSave]);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="md" variant="outlined">
        <ModalClose />
        <DialogTitle>{t("New Supplier")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <QTextInput
              label={t("Name")}
              val={supplier.name}
              update={async (name) => {
                setSupplier(Object.assign(supplier, { name }));
                return name;
              }}
            />
            <QTextInput
              label={t("Country")}
              val={supplier.country}
              update={async (country) => {
                setSupplier(Object.assign(supplier, { country }));
                return country;
              }}
            />
            <QTextInput
              label={t("Address Line 1")}
              val={supplier.addressLine1}
              update={async (addressLine1) => {
                setSupplier(Object.assign(supplier, { addressLine1 }));
                return addressLine1;
              }}
            />
            <QTextInput
              label={t("Address Line 2")}
              val={supplier.addressLine2}
              update={async (addressLine2) => {
                setSupplier(Object.assign(supplier, { addressLine2 }));
                return addressLine2;
              }}
            />
            <QTextInput
              label={t("City")}
              val={supplier.city}
              update={async (city) => {
                setSupplier(Object.assign(supplier, { city }));
                return city;
              }}
            />
            <QTextInput
              label={t("Zip Code")}
              val={supplier.postalCode}
              update={async (postalCode) => {
                setSupplier(Object.assign(supplier, { postalCode }));
                return postalCode;
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            fullWidth
            onClick={handleSave}
            disabled={saving}
            loading={loading}
          >
            {t("Save")}
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}
